import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, PhoneCall, Timer } from "lucide-react";
import { fmtTime } from "@/lib/time";
import { SET_STATUSES, CLOSE_STATUSES, isEarlierDay, willCallWaitMin, WILL_CALL_WAIT_MIN } from "@/lib/tripStatus";

const field = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

export function WillCallBadge({ trip, testid }) {
  if (trip?.status !== "Will Call") return null;
  const wait = willCallWaitMin(trip);
  const late = wait != null && wait >= WILL_CALL_WAIT_MIN;
  const cls = late ? "border-red-500/50 bg-red-500/15 text-red-300" : trip.will_call_ready_at ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-600 bg-slate-800/60 text-slate-300";
  return (
    <span data-testid={testid} title={trip.will_call_ready_at ? `Rider ready since ${fmtTime(trip.will_call_ready_at)}` : "No set pickup time — the rider calls when ready"}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {late ? <Timer className="h-3 w-3" /> : <PhoneCall className="h-3 w-3" />}
      {late ? `Waiting ${wait} min · no driver` : trip.will_call_ready_at ? "Rider ready" : "Waiting for call"}
    </span>
  );
}

export function RiderReadyButton({ trip, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await fleetApi.post(`/trips/${trip.id}/rider-ready`);
      toast.success("Rider marked ready — pickup is now and the owner has an assign request.");
      onDone?.();
    } catch (e) { toast.error(fleetError(e, "Couldn't mark the rider ready.")); } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} data-testid="trip-action-rider-ready"
      className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PhoneCall className="h-3.5 w-3.5" />} Rider is ready
    </button>
  );
}

// Change one trip's status by hand, or close out many earlier-day trips (tripIds) via Day Review bulk-status.
export function SetStatusDialog({ trip, tripIds, open, onOpenChange, onDone }) {
  const bulk = !!tripIds;
  const options = bulk ? CLOSE_STATUSES : SET_STATUSES;
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStatus(""); setReason(""); } }, [open]);
  const earlier = bulk || isEarlierDay(trip);
  const hasDriver = !!(trip?.assigned_driver_id || trip?.assigned_driver_name);

  const save = async () => {
    setBusy(true);
    try {
      if (bulk) {
        const { data } = await fleetApi.post("/day-review/bulk-status", { trip_ids: tripIds, status, reason: reason.trim() });
        toast.success(`${data.updated} marked ${status} and filed in history${data.skipped ? ` · ${data.skipped} skipped` : ""}${data.review_left != null ? ` · ${data.review_left} left in review` : ""}.`);
      } else {
        const { data } = await fleetApi.post(`/trips/${trip.id}/set-status`, { status, reason: reason.trim() || null });
        toast.success(data?.filed ? `Marked ${status} and filed in History.` : `Status changed to ${status}.`);
      }
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(fleetError(e, "Couldn't change the status.")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white sm:max-w-md" data-testid="set-status-dialog">
        <DialogHeader>
          <DialogTitle>{bulk ? `Close out ${tripIds.length} trip${tripIds.length === 1 ? "" : "s"}` : `Change status · ${trip?.rider_name || ""}`}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {bulk ? "They'll be marked and filed in History." : `Currently ${trip?.status}.${earlier ? ` This trip is from an earlier day — a reason is required${CLOSE_STATUSES.includes(status) ? ", and closing it files it to History" : ""}.` : ""}`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {options.map((s) => {
            const off = (!bulk && s === trip?.status) || (s === "Assigned" && !hasDriver && !bulk);
            return (
              <button key={s} disabled={off} onClick={() => setStatus(s)} data-testid={`set-status-${s.toLowerCase().replace(/ /g, "-")}`}
                title={s === "Assigned" && !hasDriver ? "Assign a driver first" : undefined}
                className={`rounded-md border px-3 py-2 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${status === s ? "border-blue-500 bg-blue-600/15 text-white" : "border-slate-700 text-slate-200 hover:bg-slate-800"}`}>
                {s}
              </button>
            );
          })}
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-400">Reason {earlier ? "*" : "(optional)"}</label>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={field} placeholder="e.g. Rider confirmed the ride happened; driver's phone died" data-testid="set-status-reason" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="rounded-md px-4 py-2 text-sm text-slate-300 hover:bg-slate-800" data-testid="set-status-cancel">Cancel</button>
          <button onClick={save} disabled={busy || !status || (earlier && !reason.trim())} data-testid="set-status-save"
            className="inline-flex items-center gap-2 rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {status ? `Mark ${status}` : "Pick a status"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
